import React, { useContext, useEffect, useState } from 'react';
import { fmtDate as _fmtDate } from '@/lib/dates';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { fmtDate } from '@/components/payroll/payrollFmt';
import { fmtDateStr, statusSeverity, SEVERITY, payrollTypeLabel } from '@/components/payroll/payrollFmt';
import { CalendarRange, Plus, AlertTriangle, AlertCircle, Bell, CheckCircle2, Users, Clock, FileText, RotateCcw } from 'lucide-react';

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });
const FIELD = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const RATE_BASIS_LABELS = { hourly: 'Hourly', daily: 'Daily', flat_unit: 'Flat / Unit', monthly_location: 'Monthly Location Amount', weekly_flat: 'Weekly-Flat' };
const AP_KINDS = { bonus_eom: 'Bonus / End-of-Month', prior_period_correction: 'Prior-Period Correction', other_approved: 'Other Approved Pay', work_order: 'Work-Order Pay' };

const STATUS_ORDER = ['draft', 'prepared', 'pending_owner_approval', 'approved_submitted', 'ready_for_qb_entry', 'entered_in_quickbooks', 'closed'];
const STATUS = {
  draft: { t: 'Draft', c: 'bg-slate-100 text-slate-700' },
  prepared: { t: 'Prepared', c: 'bg-blue-100 text-blue-700' },
  pending_owner_approval: { t: 'Pending Final Payroll Approval', c: 'bg-amber-100 text-amber-700' },
  approved_submitted: { t: 'Finally Approved', c: 'bg-emerald-100 text-emerald-700' },
  ready_for_qb_entry: { t: 'Ready for Processing', c: 'bg-indigo-100 text-indigo-700' },
  entered_in_quickbooks: { t: 'Processed (QuickBooks)', c: 'bg-violet-100 text-violet-700' },
  closed: { t: 'Closed', c: 'bg-slate-200 text-slate-600' },
};
const StatusBadge = ({ s }) => {
  const x = STATUS[s] || STATUS.draft;
  return <span className={`rounded-full px-2.5 py-0.5 text-sm font-semibold ${x.c}`} data-testid="run-status-badge">{x.t}</span>;
};
const reopenTargets = (cur) => STATUS_ORDER.slice(0, Math.max(0, STATUS_ORDER.indexOf(cur)));

// ---- Preparation Worksheet helpers ----
const CADENCE_LABELS = { first_half: 'First-Half', second_half: 'Second-Half', monthly: 'Monthly' };
const nnum = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const rateFixed = (en) => {
  if (en.rate_basis === 'monthly_location') return en.approved_monthly_payout != null ? `$${Number(en.approved_monthly_payout).toFixed(2)}/mo` : '—';
  if (en.rate_basis === 'hourly') return en.rate != null ? `$${Number(en.rate).toFixed(2)}/hr` : '—';
  if (en.rate_basis === 'weekly_flat') return en.weekly_reference_amount != null ? `$${Number(en.weekly_reference_amount).toFixed(2)}/wk` : '—';
  return en.rate != null ? `$${Number(en.rate).toFixed(2)}/day` : '—';
};
const expectedUnits = (en) => {
  if (en.rate_basis === 'monthly_location') return en.normal_labor_days_auto != null ? `${en.normal_labor_days_auto} d` : '—';
  if (en.rate_basis === 'hourly') return '—';
  return '—';
};
const actualUnits = (en) => {
  const a = en.actuals; if (!a) return null;
  const v = a.hours ?? a.payable_days ?? a.normal_labor_days ?? a.units;
  if (v == null) return null;
  return en.rate_basis === 'hourly' ? `${v} h` : `${v} d`;
};
const woPay = (en) => (en.additional_pay || []).filter((l) => l.kind === 'work_order').reduce((s, l) => s + (l.amount || 0), 0);
const groupByAccount = (entries) => {
  const m = {};
  (entries || []).forEach((en) => {
    const k = en.account_number || '—';
    (m[k] = m[k] || { account_number: en.account_number, account_name: en.account_name, rows: [] }).rows.push(en);
  });
  return Object.values(m).sort((a, b) => String(a.account_number || 'zzz').localeCompare(String(b.account_number || 'zzz')));
};
const WS_TH = 'px-2 py-1.5 text-left font-semibold text-slate-500 whitespace-nowrap';
const WS_TD = 'px-2 py-1.5 whitespace-nowrap';
// Miami-date-aware milestone states (never "Overdue" before the Miami date passes)
const MSTATE = {
  completed: { t: 'Completed', c: 'text-emerald-700', chip: 'bg-emerald-100 text-emerald-700' },
  due_today: { t: 'Due Today', c: 'text-amber-700 font-semibold', chip: 'bg-amber-100 text-amber-700' },
  upcoming: { t: 'Upcoming', c: 'text-slate-500', chip: 'bg-slate-100 text-slate-600' },
  past_due: { t: 'Past Due', c: 'text-red-600 font-semibold', chip: 'bg-red-100 text-red-700' },
};
const mstate = (s) => MSTATE[s] || MSTATE.upcoming;


const RISK = {
  period_open: 'Payroll Period Open',
  closes_today: 'Closes Today — Preparation May Begin',
  preparation_due: 'Preparation Due Today',
  in_progress: 'Payroll in Progress',
  due_today: 'Due Today',
  deadline_may_be_missed: 'Deadline May Be Missed',
  past_due: 'Past Due',
  rollout: 'Initial Rollout',
  submitted: 'Submitted',
  closed: 'Closed',
};
const SEV_ICON = { reminder: Bell, action: AlertCircle, critical: AlertTriangle, complete: CheckCircle2 };
const RiskBadge = ({ s }) => {
  const sev = SEVERITY[statusSeverity(s)];
  const Icon = SEV_ICON[statusSeverity(s)];
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-sm font-semibold ${sev.badge}`} data-testid="run-risk-badge">
      <Icon size={11} />{RISK[s] || 'Payroll Period Open'}
    </span>
  );
};

const RunField = ({ label, children }) => (
  <div>
    <span className="block text-xs font-medium uppercase tracking-wide text-slate-400">{label}</span>
    <div className="mt-1 text-base font-semibold text-[#0E2A47]">{children}</div>
  </div>
);

// ---- Account-by-account Preparation Workspace ----
const ACCT_STATUS = {
  not_reviewed: { t: 'Not Reviewed', c: 'bg-slate-200 text-slate-700', dot: 'bg-slate-400' },
  needs_attention: { t: 'Needs Attention', c: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  ready_to_confirm: { t: 'Ready to Confirm', c: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  confirmed: { t: 'Confirmed', c: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
};
const usd = (n) => (n === null || n === undefined ? '—' : `$${Number(n).toFixed(2)}`);
const budgetColor = { under: 'text-amber-700', over: 'text-red-700', on: 'text-emerald-700', na: 'text-slate-400' };

// Reusable per-line budget-status pill styles (Phase 2 vocabulary).
const BUDGET_STATUS_STYLE = {
  'On Budget': 'bg-emerald-100 text-emerald-700',
  'Approved Increase': 'bg-blue-100 text-blue-700',
  'Negotiated Savings': 'bg-teal-100 text-teal-700',
  'Partial-Month Startup': 'bg-indigo-100 text-indigo-700',
  'Missing Payroll Compensation': 'bg-amber-100 text-amber-800',
  'Budget Mapping Required': 'bg-amber-100 text-amber-800',
  'Unallocated Planned Labor': 'bg-slate-200 text-slate-700',
  'Unexplained Difference': 'bg-red-100 text-red-700',
};
const budgetStatusStyle = (s) => BUDGET_STATUS_STYLE[s] || 'bg-slate-200 text-slate-700';

// Confirmation status (separate from automatic budget reconciliation).
const confLabel = (e) => (e.confirmation_status === 'confirmed' ? 'Confirmed'
  : (e.expected || {}).missing ? 'Information Required' : 'Ready to Confirm');
const confStyle = (e) => (e.confirmation_status === 'confirmed' ? 'bg-emerald-100 text-emerald-700'
  : (e.expected || {}).missing ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600');

const SummaryStat = ({ label, value, sub, tone }) => (
  <div className="min-w-[120px]">
    <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</div>
    <div className={`text-lg font-bold ${tone || 'text-[#0E2A47]'}`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
  </div>
);

// Account-level location budget reconciliation summary (Phase-1 authoritative source).
const ReconSummary = ({ r, acct }) => (
  <div className="mt-3 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4" data-testid={`acct-recon-summary-${acct}`}>
    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
      <div className="text-sm font-bold uppercase tracking-wide text-slate-500">Location Budget Reconciliation</div>
      <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700" data-testid={`acct-recon-status-${acct}`}>{r.status}</span>
    </div>
    <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
      <SummaryStat label="Total Labor Budget" value={usd(r.total_labor_budget)} />
      <SummaryStat label="Assigned Location Budget" value={usd(r.assigned_location_budget)} />
      <SummaryStat label="Unallocated Planned Labor" value={usd(r.unallocated_planned_labor)} tone="text-slate-500" />
      <SummaryStat label="Projected Payroll" value={usd(r.projected_payroll)} />
      <SummaryStat label="Negotiated Savings" value={usd(r.negotiated_savings)} tone="text-teal-700" />
      <SummaryStat label="Partial-Month / Unused" value={usd(r.partial_month_unused)} tone="text-indigo-700" />
      <SummaryStat label="Total Variance" value={`${usd(r.under_budget_variance)} Under`} tone="text-amber-700" />
      <SummaryStat label="Unexplained" value={usd(r.unexplained)} tone={Math.abs(r.unexplained || 0) < 0.005 ? 'text-emerald-700' : 'text-red-700'} />
    </div>
    {r.unallocated_lines?.length > 0 && (
      <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3" data-testid={`acct-unallocated-${acct}`}>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Unallocated Planned Labor (account level)</div>
        <div className="mt-1 flex flex-wrap gap-x-8 gap-y-1 text-sm">
          {r.unallocated_lines.map((u, i) => (
            <span key={i} className="text-slate-600" data-testid={`unalloc-line-${acct}-${i}`}>{u.location} · <b className="text-[#0E2A47]">{usd(u.amount)}</b></span>
          ))}
        </div>
      </div>
    )}
  </div>
);

const EntryCalcCard = ({ e, canEnter, isDraft, onConfirm, onEdit }) => {
  const exp = e.expected || {};
  const confirmed = e.confirmation_status === 'confirmed';
  const missing = exp.missing;
  const br = e.budget_recon;
  const wo = (e.additional_pay || []).filter((l) => l.kind === 'work_order').reduce((s, l) => s + (l.amount || 0), 0);
  const other = (e.additional_pay || []).filter((l) => l.kind !== 'work_order').reduce((s, l) => s + (l.amount || 0), 0);
  const reg = confirmed && e.components ? (e.components.base_component ?? 0) : exp.expected_regular;
  const finalGross = confirmed && e.gross != null ? e.gross : (exp.expected_regular || 0) + wo + other;
  return (
    <div className={`rounded-lg border p-3 ${confirmed ? 'border-emerald-200 bg-emerald-50/40' : missing ? 'border-red-200 bg-red-50/40' : 'border-slate-200 bg-white'}`} data-testid={`ws-entry-${e.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-[#0E2A47]">{e.payee_name}
            {e.is_exception && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">Adjusted</span>}
            {confirmed && <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">Confirmed</span>}
          </div>
          {e.location_name && <div className="text-base font-bold text-slate-800" data-testid={`ws-loc-${e.id}`}>{e.location_name}</div>}
          <div className="text-[11px] text-slate-400">{e.classification || (e.payee_type === 'work_order' ? 'Work-Order' : '')} · {CADENCE_LABELS[e.cadence] || ''} · {RATE_BASIS_LABELS[e.rate_basis] || e.rate_basis}</div>
        </div>
        <div className="flex items-start gap-3">
          {br && <div className="flex flex-col items-end gap-1">
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${budgetStatusStyle(br.status)}`} data-testid={`ws-budget-status-${e.id}`}>{br.status}</span>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${confStyle(e)}`} data-testid={`ws-conf-status-${e.id}`}>{confLabel(e)}</span>
          </div>}
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Final Gross</div>
            <div className="text-lg font-bold text-[#0E2A47]" data-testid={`ws-final-${e.id}`}>{usd(finalGross)}</div>
          </div>
        </div>
      </div>
      {br && (
        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 rounded-lg bg-slate-50 px-3 py-2 text-xs sm:grid-cols-4" data-testid={`ws-budget-recon-${e.id}`}>
          <div><span className="text-slate-400">Location Budget</span><div className="font-semibold text-[#0E2A47]">{usd(br.location_budget)}</div></div>
          <div><span className="text-slate-400">Approved</span><div className="font-semibold text-[#0E2A47]">{usd(br.approved)}</div></div>
          <div><span className="text-slate-400">Projected</span><div className="font-semibold text-[#0E2A47]">{usd(exp.expected_regular)}</div></div>
          <div><span className="text-slate-400">Difference</span><div className={`font-semibold ${br.difference > 0.005 ? 'text-red-600' : br.difference < -0.005 ? 'text-amber-600' : 'text-emerald-600'}`}>{Math.abs(br.difference) < 0.005 ? '$0.00' : `${usd(Math.abs(br.difference))} ${br.difference > 0 ? 'Over' : 'Under'}`}</div></div>
          {br.partial_month && (
            <div className="col-span-2 mt-1 rounded bg-indigo-50 px-2 py-1 text-[11px] text-indigo-800 sm:col-span-4" data-testid={`ws-partial-${e.id}`}>
              Partial-month startup — full-month budget {usd(br.location_budget)} · service commenced {fmtDate(e.effective_start_date)} · eligible working days {br.normal_working_days} · authorized service days {br.authorized_service_days} · unused budget <b>{usd(br.unused)}</b>
            </div>
          )}
        </div>
      )}
      {missing ? (
        <div className="mt-2 rounded bg-red-100 px-2 py-1 text-xs font-semibold text-red-700" data-testid={`ws-missing-${e.id}`}>{exp.missing_reason || 'Information required'} — enter before confirming.</div>
      ) : exp.basis === 'salary' ? (
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
          <span><span className="text-slate-400">Salary basis:</span> <b>{exp.breakdown?.[0]?.[1]}</b></span>
          <span><span className="text-slate-400">Pay-period salary:</span> <b>{usd(exp.salary_per_period)}</b></span>
          <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700">Holiday treatment: No Deduction</span>
        </div>
      ) : (
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
          {(exp.breakdown || []).map(([k, v], i) => (
            <span key={i}><span className="text-slate-400">{k}:</span> <b>{String(v)}</b></span>
          ))}
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-2">
        <div className="text-xs font-mono text-slate-500" data-testid={`ws-formula-${e.id}`}>{exp.formula}
          {(wo || other) ? <span className="ml-2 text-slate-400">+ {usd(wo + other)} additional/WO</span> : null}
        </div>
        {isDraft && canEnter && (
          <div className="flex items-center gap-2">
            <button className="rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50" data-testid={`ws-edit-${e.id}`} onClick={() => onEdit(e)}>Edit exception</button>
            {!confirmed && !missing && <button className="rounded bg-[#0E2A47] px-2.5 py-1 text-xs font-semibold text-white hover:bg-[#123a63]" data-testid={`ws-confirm-${e.id}`} onClick={() => onConfirm(e)}>Confirm</button>}
          </div>
        )}
        {!isDraft && <span className="text-xs text-slate-400">Regular {usd(reg)}</span>}
      </div>
    </div>
  );
};

const PrepWorkspace = ({ ws, entries, sel, setSel, canEnter, isDraft, onConfirmEntry, onConfirmAccount, onOpenEntry }) => {
  const [showVar, setShowVar] = React.useState(false);
  const accts = ws.accounts || [];
  const s = ws.summary || {};
  const cur = accts.find((a) => String(a.account_number) === String(sel)) || accts[0];
  const curEntries = entries.filter((e) => String(e.account_number) === String(cur?.account_number));
  const idx = accts.findIndex((a) => a === cur);
  return (
    <div data-testid="prep-workspace">
      {/* Payroll-level summary */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="payroll-summary">
        <div className="flex flex-wrap gap-6">
          {s.budget_complete
            ? <SummaryStat label="Total Budget" value={usd(s.total_budget)} sub="all accounts mapped" />
            : <SummaryStat label="Budget" value="Incomplete" tone="text-amber-700" sub={`${s.budget_missing_accounts?.length || 0} account(s) unmapped`} />}
          <SummaryStat label="Projected Payroll" value={usd(s.projected)} sub={`${s.people} people`} />
          <SummaryStat label="Confirmed Payroll" value={usd(s.confirmed_amount)} tone="text-emerald-700" sub="committed" />
          <SummaryStat label="Remaining Unconfirmed" value={usd(s.unconfirmed_amount)} tone="text-amber-700" sub="awaiting confirmation" />
          {s.budget_complete
            ? <SummaryStat label="Variance" value={Math.abs(s.variance || 0) < 0.005 ? 'On Budget' : `${usd(Math.abs(s.variance))} ${s.variance > 0 ? 'Over' : 'Under'}`} tone={s.variance > 0.005 ? 'text-red-700' : s.variance < -0.005 ? 'text-amber-700' : 'text-emerald-700'} sub="mapped accounts" />
            : <SummaryStat label="Budget Comparison" value="Incomplete" tone="text-amber-700" sub={`mapped ${usd(s.mapped_projected)} · unmapped ${usd(s.unmapped_projected)}`} />}
          <SummaryStat label="Accounts Confirmed" value={`${s.accounts_confirmed}/${s.accounts_total}`} sub={`${s.accounts_remaining} remaining`} />
          <SummaryStat label="Exceptions" value={s.exceptions} tone={s.exceptions ? 'text-red-700' : 'text-emerald-700'} sub="need attention" />
        </div>
        {!s.budget_complete && <div className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800" data-testid="budget-incomplete-note">Budget Comparison Incomplete — one or more accounts (e.g. {String((s.budget_missing_accounts || []).join(', '))}) have no completed budget. Overall variance is withheld until every account is mapped.</div>}
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${s.accounts_total ? (s.accounts_confirmed / s.accounts_total) * 100 : 0}%` }} data-testid="progress-bar" />
        </div>
        <div className="mt-1 text-xs text-slate-500" data-testid="progress-text">{s.accounts_confirmed} of {s.accounts_total} accounts confirmed · {s.exceptions} exception{s.exceptions === 1 ? '' : 's'} remaining</div>
      </div>

      {ws.holidays && ws.holidays.length > 0 ? (
        <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm" data-testid="holiday-setup">
          <div className="font-semibold text-indigo-800">Payroll Setup — Holidays in this period</div>
          {ws.holidays.map((h) => <div key={h.date} className="text-indigo-700">{h.name} · {_fmtDate(h.date)} — paid-holiday rules applied per account &amp; pay method</div>)}
        </div>
      ) : (
        <div className="mt-3 text-xs text-slate-400" data-testid="holiday-none">No holidays fall within this payroll period.</div>
      )}

      {/* Account navigator */}
      <div className="mt-4 flex flex-wrap gap-2" data-testid="account-nav">
        {accts.map((a) => {
          const st = ACCT_STATUS[a.status] || ACCT_STATUS.not_reviewed;
          const active = a === cur;
          return (
            <button key={a.account_number || 'na'} onClick={() => setSel(a.account_number)} data-testid={`acct-tab-${a.account_number}`}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-all ${active ? 'border-[#0E2A47] bg-[#0E2A47] text-white shadow' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
              <span className={`h-2 w-2 rounded-full ${st.dot}`} />
              <span>
                <span className="block text-sm font-bold leading-tight">{a.account_number}</span>
                <span className={`block text-[11px] leading-tight ${active ? 'text-slate-200' : 'text-slate-500'}`}>{a.account_name} · {a.people}p</span>
              </span>
              <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${active ? 'bg-white/20 text-white' : st.c}`}>{st.t}</span>
            </button>
          );
        })}
      </div>

      {/* Selected account panel */}
      {cur && (
        <div className="mt-3 rounded-xl border border-slate-200 bg-white shadow-sm" data-testid={`acct-panel-${cur.account_number}`}>
          <div className="border-b border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-lg font-bold text-[#0E2A47]">{cur.account_number} · {cur.account_name}</div>
                <div className="text-xs text-slate-500">{cur.people} people/companies · {cur.confirmed} confirmed · {cur.exceptions} exception{cur.exceptions === 1 ? '' : 's'}{cur.holiday_applied ? ' · holiday rules applied' : ''}</div>
              </div>
              <div className="flex flex-wrap gap-5">
                <SummaryStat label="Labor Budget" value={cur.labor_budget != null ? usd(cur.labor_budget) : (cur.incomplete_budget ? 'Incomplete' : 'N/A')} tone={cur.labor_budget == null ? 'text-slate-400' : undefined} />
                <SummaryStat label="Approved" value={usd(cur.approved_expected)} />
                <SummaryStat label="Projected" value={usd(cur.projected)} />
                <SummaryStat label="Confirmed" value={usd(cur.confirmed_amount)} tone="text-emerald-700" />
                <SummaryStat label="Variance" value={cur.variance != null ? (Math.abs(cur.variance) < 0.005 ? 'On Budget' : `${usd(Math.abs(cur.variance))} ${cur.variance > 0 ? 'Over' : 'Under'}`) : '—'} tone={budgetColor[cur.budget_state]} />
              </div>
            </div>
            {cur.budget?.derivation && <div className="mt-2 text-[11px] text-slate-500" data-testid={`acct-budget-deriv-${cur.account_number}`}>Budget basis: {cur.budget.derivation}</div>}
            {cur.reconciliation && <ReconSummary r={cur.reconciliation} acct={cur.account_number} />}
            {cur.budget_state === 'na' && <div className="mt-2 rounded bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600" data-testid={`acct-budget-na-${cur.account_number}`}>{cur.incomplete_budget ? cur.budget_label : 'Budget Not Available — Mapping Required'}</div>}
            {cur.under_warning && <div className="mt-2 rounded bg-amber-100 px-2 py-1 text-xs text-amber-800" data-testid={`acct-under-warn-${cur.account_number}`}>⚠ {cur.budget_label}</div>}
            {cur.budget_state === 'over' && cur.over_causes?.length > 0 && (
              <div className="mt-2 rounded bg-red-50 px-2 py-1 text-xs text-red-700" data-testid={`acct-over-cause-${cur.account_number}`}>Over budget caused by: {cur.over_causes.map((c, i) => `${c.payee} (${c.amount >= 0 ? '+' : '−'}${usd(Math.abs(c.amount))} ${c.reason})`).join('; ')}</div>
            )}
            {(cur.budget_state === 'over' || cur.budget_state === 'under') && cur.variance_detail && (
              <div className="mt-2">
                <button className="text-xs font-semibold text-[#0E2A47] underline" data-testid={`view-variance-${cur.account_number}`} onClick={() => setShowVar((v) => !v)}>{showVar ? 'Hide' : 'View'} Variance Details</button>
                {showVar && (
                  <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs" data-testid={`variance-detail-${cur.account_number}`}>
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      {cur.variance_detail.label && <span className={`rounded px-2 py-0.5 font-semibold ${cur.variance_detail.label.startsWith('Confirmed') ? 'bg-emerald-100 text-emerald-700' : cur.variance_detail.label.startsWith('Likely') ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-700'}`} data-testid={`variance-label-${cur.account_number}`}>{cur.variance_detail.label}</span>}
                      <span className="text-slate-500">Budget ${Number(cur.variance_detail.budget_amount).toFixed(2)} · Approved ${Number(cur.variance_detail.approved_total).toFixed(2)} · Explained ${Number(cur.variance_detail.explained).toFixed(2)} · Unexplained ${Number(cur.variance_detail.unexplained).toFixed(2)}</span>
                    </div>
                    {cur.variance_detail.budget_version != null && <div className="text-[11px] text-slate-400">Budget source: Contract Budget v{cur.variance_detail.budget_version}{cur.variance_detail.budget_period ? ` · ${cur.variance_detail.budget_period}` : ''}{cur.variance_detail.budget_updated ? ` · updated ${_fmtDate(cur.variance_detail.budget_updated)}` : ''}</div>}
                    {cur.variance_detail.savings_total != null && (
                      <div className="mt-1 text-[11px] text-slate-600" data-testid={`variance-counts-${cur.account_number}`}>Baseline ${Number(cur.variance_detail.baseline_total).toFixed(2)} + increases ${Number(cur.variance_detail.increases_total).toFixed(2)} = revised ${Number(cur.variance_detail.budget_amount).toFixed(2)} · {cur.variance_detail.exact_matches} exact · {cur.variance_detail.increase_count} increases · {cur.variance_detail.savings_count} savings (${Number(cur.variance_detail.savings_total).toFixed(2)}) · {cur.variance_detail.unresolved_count} unresolved</div>
                    )}
                    {cur.variance_detail.has_line_detail ? (
                      <table className="mt-2 w-full">
                        <thead><tr className="text-left text-slate-400"><th className="py-1">Payee</th><th>Location</th><th className="text-right">Budget</th><th className="text-right">Payroll</th><th className="text-right">Diff</th><th className="text-right">Savings</th><th>Status</th></tr></thead>
                        <tbody>
                          {cur.variance_detail.contributors.map((c, i) => (
                            <tr key={i} className="border-t border-slate-200" data-testid={`variance-row-${cur.account_number}-${i}`}>
                              <td className="py-1">{c.payee || '—'}</td>
                              <td className="font-medium text-slate-700">{c.location}</td>
                              <td className="text-right">{usd(c.budgeted)}</td>
                              <td className="text-right">{usd(c.approved)}</td>
                              <td className={`text-right font-semibold ${c.difference > 0 ? 'text-red-600' : c.difference < 0 ? 'text-amber-600' : 'text-slate-400'}`}>{c.difference > 0 ? '+' : c.difference < 0 ? '−' : ''}{usd(Math.abs(c.difference))}</td>
                              <td className="text-right text-emerald-700">{c.savings ? usd(c.savings) : '—'}</td>
                              <td className="text-[10px]"><span className={`rounded px-1.5 py-0.5 ${c.status === 'Approved Compensation Increase' ? 'bg-blue-100 text-blue-700' : c.status === 'Negotiated Savings' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{c.status}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="mt-1 text-slate-600" data-testid={`variance-setup-${cur.account_number}`}>{cur.variance_detail.setup_note}</div>
                    )}
                    {canEnter && (
                      <button className="mt-2 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-100" data-testid={`review-budget-${cur.account_number}`} onClick={() => toast.info('Opens the Contract Budget for review — budget changes require an authorized user, effective date and reason (not changed here).')}>Review Budget Update</button>
                    )}
                  </div>
                )}
              </div>
            )}
            {isDraft && canEnter && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button className="rounded-lg bg-[#0E2A47] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#123a63]" data-testid={`confirm-account-${cur.account_number}`} onClick={() => onConfirmAccount(cur)}>Confirm Account ({cur.unconfirmed} unchanged)</button>
                <button disabled={idx <= 0} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 disabled:opacity-40" data-testid="acct-prev" onClick={() => setSel(accts[idx - 1]?.account_number)}>← Previous</button>
                <button disabled={idx >= accts.length - 1} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 disabled:opacity-40" data-testid="acct-next" onClick={() => setSel(accts[idx + 1]?.account_number)}>Next →</button>
              </div>
            )}
          </div>
          <div className="space-y-2 p-4">
            {curEntries.map((e) => (
              <EntryCalcCard key={e.id} e={e} canEnter={canEnter} isDraft={isDraft} onConfirm={onConfirmEntry} onEdit={onOpenEntry} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


const PayrollPayRuns = ({ meta }) => {
  const { token, user } = useContext(AuthContext);
  const canPrepare = meta?.can_prepare;
  const canProcess = meta?.can_process;
  const canApprove = meta?.can_approve;
  const canEnter = meta?.can_enter_assigned || canPrepare;
  const [tab, setTab] = useState('runs');
  const [runs, setRuns] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [editEntry, setEditEntry] = useState(null);
  const [selAcct, setSelAcct] = useState(null);
  const [ackWarn, setAckWarn] = useState(false);
  const [act, setAct] = useState({});
  const [ap, setAp] = useState({ kind: 'bonus_eom' });

  // Phase 5 workflow state
  const [qbTotal, setQbTotal] = useState('');
  const [qbFile, setQbFile] = useState(null);
  const [replaceReason, setReplaceReason] = useState('');
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [reopenTo, setReopenTo] = useState('');
  const [qbInputKey, setQbInputKey] = useState(0);
  const [reopenReason, setReopenReason] = useState('');

  const now = new Date();
  const [form, setForm] = useState({ group: 'first_half', year: now.getFullYear(), month: now.getMonth() + 1, target_funding_date: '' });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  // Summary Packet
  const [pk, setPk] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [packet, setPacket] = useState(null);

  const loadRuns = async () => {
    setLoading(true);
    try { setRuns((await axios.get(`${API}/payroll/runs`, auth(token))).data); }
    catch (e) { toast.error('Failed to load runs'); } finally { setLoading(false); }
  };
  const loadCalendar = async () => {
    try { setCalendar((await axios.get(`${API}/payroll/calendar?months=6`, auth(token))).data.events || []); }
    catch (e) { /* noop */ }
  };
  const loadReminders = async () => {
    try { setReminders((await axios.get(`${API}/payroll/reminders`, auth(token))).data.reminders || []); }
    catch (e) { /* noop */ }
  };
  useEffect(() => { loadRuns(); loadCalendar(); loadReminders(); /* eslint-disable-next-line */ }, []);

  const doPreview = async (f, keepTarget = false) => {
    try {
      const q = `group=${f.group}&year=${f.year}&month=${f.month}` +
        (keepTarget && f.target_funding_date ? `&target_funding_date=${f.target_funding_date}` : '');
      const { data } = await axios.get(`${API}/payroll/runs/preview?${q}`, auth(token));
      setPreview(data);
      if (!keepTarget) setForm((p) => ({ ...p, target_funding_date: data.dates.target_funding_date }));
    } catch (e) { setPreview(null); }
  };
  const openCreate = () => { setForm((p) => ({ ...p, target_funding_date: '' })); setPreview(null); setOpen(true); doPreview(form); };
  const onField = (k, v) => { const f = { ...form, [k]: v }; setForm(f); if (['group', 'year', 'month'].includes(k)) doPreview(f); };
  const chooseFunding = (t) => { const f = { ...form, target_funding_date: t }; setForm(f); doPreview(f, true); };

  const create = async () => {
    setSaving(true);
    try {
      const body = { group: form.group, year: Number(form.year), month: Number(form.month), target_funding_date: form.target_funding_date || null };
      const { data } = await axios.post(`${API}/payroll/runs`, body, auth(token));
      toast.success(`Draft run created (${data.payee_count} payees)`);
      setOpen(false); loadRuns(); loadReminders();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create run'); }
    finally { setSaving(false); }
  };

  const openEntry = (en) => { setEditEntry(en); setAct(en.actuals || {}); setAp({ kind: 'bonus_eom', account_number: en.account_number, account_name: en.account_name }); };
  const saveActuals = async () => {
    try {
      await axios.put(`${API}/payroll/runs/${detail.id}/entries/${editEntry.id}/actuals`,
        { actuals: act, reason: act._reason, confirm_normal_days: true }, auth(token));
      toast.success('Gross saved');
      const d = (await axios.get(`${API}/payroll/runs/${detail.id}`, auth(token))).data;
      setDetail(d); setEditEntry(d.entries.find((x) => x.id === editEntry.id) || null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
  };
  const addAdditional = async () => {
    try {
      await axios.post(`${API}/payroll/runs/${detail.id}/entries/${editEntry.id}/additional-pay`, ap, auth(token));
      toast.success('Additional pay added');
      const d = (await axios.get(`${API}/payroll/runs/${detail.id}`, auth(token))).data;
      setDetail(d); setEditEntry(d.entries.find((x) => x.id === editEntry.id) || null);
      setAp({ kind: 'bonus_eom', account_number: editEntry.account_number, account_name: editEntry.account_name });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const openRun = async (id) => {
    setQbTotal(''); setQbFile(null); setReplaceReason(''); setQbInputKey((k) => k + 1); setOverride(false); setOverrideReason(''); setReopenTo(''); setReopenReason(''); setSelAcct(null); setAckWarn(false);
    try { setDetail((await axios.get(`${API}/payroll/runs/${id}`, auth(token))).data); }
    catch (e) { toast.error('Failed to open run'); }
  };
  const refreshDetail = async (keepEntry) => {
    const d = (await axios.get(`${API}/payroll/runs/${detail.id}`, auth(token))).data;
    setDetail(d);
    if (keepEntry && editEntry) setEditEntry(d.entries.find((x) => x.id === editEntry.id) || null);
    return d;
  };
  const confirmEntry = async (e) => {
    try { await axios.post(`${API}/payroll/runs/${detail.id}/entries/${e.id}/confirm`, {}, auth(token)); toast.success(`${e.payee_name} confirmed`); await refreshDetail(); loadRuns(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed to confirm'); }
  };
  const confirmAccount = async (a) => {
    try {
      const { data } = await axios.post(`${API}/payroll/runs/${detail.id}/accounts/${a.account_number}/confirm`, {}, auth(token));
      toast.success(`Account ${a.account_number}: ${data.confirmed} confirmed${data.skipped.length ? `, ${data.skipped.length} need attention` : ''}`);
      const d = await refreshDetail(); loadRuns();
      // auto-advance to next unconfirmed account
      const next = (d.workspace?.accounts || []).find((x) => x.status !== 'confirmed');
      if (next && data.skipped.length === 0) setSelAcct(next.account_number);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to confirm account'); }
  };

  // ---- Phase 5 workflow actions ----
  const doTransition = async (action, extra = {}) => {
    try {
      const { data } = await axios.post(`${API}/payroll/runs/${detail.id}/transition`, { action, ...extra }, auth(token));
      setDetail(data); loadRuns(); loadReminders();
      setQbTotal(''); setOverride(false); setOverrideReason('');
      toast.success('Workflow updated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Transition failed'); }
  };
  const uploadQbReport = async () => {
    if (!qbFile || qbTotal === '') { toast.error('Choose a file and enter the QuickBooks total'); return; }
    const hasVersions = (detail.qb_reports || []).length > 0;
    if (hasVersions && !replaceReason.trim()) { toast.error('A reason is required to replace the current report'); return; }
    const fd = new FormData();
    fd.append('file', qbFile);
    fd.append('qb_total', String(Number(qbTotal)));
    fd.append('replace_reason', replaceReason);
    try {
      const { data } = await axios.post(`${API}/payroll/runs/${detail.id}/qb-report`, fd,
        { headers: { Authorization: `Bearer ${token}` } });
      setDetail(data); loadRuns();
      setQbFile(null); setQbTotal(''); setReplaceReason(''); setQbInputKey((k) => k + 1);
      toast.success(hasVersions ? 'New report version uploaded' : 'QuickBooks report uploaded');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
  };
  const viewQbReport = async (version) => {
    try {
      const url = `${API}/payroll/runs/${detail.id}/qb-report` + (version ? `?version=${version}` : '');
      const r = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch (e) { toast.error('Could not open report'); }
  };
  const reopenRun = async () => {
    try {
      const { data } = await axios.post(`${API}/payroll/runs/${detail.id}/reopen`,
        { to_status: reopenTo, reason: reopenReason }, auth(token));
      setDetail(data); loadRuns(); setReopenTo(''); setReopenReason(''); toast.success('Run reopened');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reopen failed'); }
  };

  // ---- Summary Packet ----
  const loadPacket = async () => {
    try { setPacket((await axios.get(`${API}/payroll/packet?year=${pk.year}&month=${pk.month}`, auth(token))).data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load packet'); }
  };
  const openPacketPdf = async () => {
    try {
      const r = await axios.get(`${API}/payroll/packet.pdf?year=${pk.year}&month=${pk.month}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch (e) { toast.error('Could not open PDF'); }
  };

  const TabBtn = ({ k, label }) => (
    <button onClick={() => setTab(k)} data-testid={`payruns-tab-${k}`}
      className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${tab === k ? 'bg-[#0E2A47] text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{label}</button>
  );

  const variance = detail?.variance ?? null;
  const varianceOk = variance == null || Math.abs(variance) < 0.005;

  return (
    <div data-testid="payroll-pay-runs">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarRange className="text-[#E08A3C]" />
          <h2 className="text-2xl font-bold text-[#0E2A47]">Payroll Runs</h2>
        </div>
        {canPrepare && (
          <Button onClick={openCreate} data-testid="run-create-btn" className="bg-[#0E2A47] hover:bg-[#123a63]">
            <Plus size={16} className="mr-1" /> Create Draft Run
          </Button>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2 border-b border-slate-200 pb-3">
        <TabBtn k="runs" label="Payroll Runs" />
        <TabBtn k="calendar" label="Payroll Calendar" />
        <TabBtn k="reminders" label="Reminders" />
        <TabBtn k="packet" label="Summary Packet" />
      </div>

      {/* ---- RUNS LIST (responsive cards) ---- */}
      {tab === 'runs' && (
        <div className="mt-4" data-testid="runs-list">
          {loading ? (
            <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">Loading…</div>
          ) : runs.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500" data-testid="runs-empty">No payroll runs yet. Create a draft run to begin.</div>
          ) : (
            <div className="space-y-3">
              {runs.map((r) => (
                <button key={r.id} type="button" onClick={() => openRun(r.id)} data-testid={`run-row-${r.id}`}
                  className="block w-full rounded-xl border border-slate-200 bg-white p-5 text-left transition-colors hover:border-[#E08A3C] hover:bg-slate-50">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="text-lg font-bold text-[#0E2A47]">{payrollTypeLabel(r.group)}</div>
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge s={r.status} />
                      <RiskBadge s={r.cycle_risk?.status} />
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-3 xl:grid-cols-5">
                    <RunField label="Payroll Period">{fmtDate(r.period_start)} → {fmtDate(r.period_end)}</RunField>
                    <RunField label="Scheduled Payday">{fmtDate(r.nominal_payday)}{r.needs_funding_confirmation && <span className="ml-1 text-amber-600" title="Adjusted for weekend/holiday">*</span>}</RunField>
                    <RunField label="Expected Payment Date">{fmtDate(r.target_funding_date)}</RunField>
                    <RunField label="Send to QuickBooks By">{fmtDate(r.qb_submission_cutoff)}</RunField>
                    <RunField label="People and Companies"><span className="inline-flex items-center gap-1.5"><Users size={15} />{r.payee_count}</span></RunField>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---- CALENDAR ---- */}
      {tab === 'calendar' && (
        <div className="mt-4 max-h-[560px] space-y-3 overflow-y-auto pr-2" data-testid="payroll-calendar">
          {calendar.filter((e) => e.type === 'pay_cycle').map((e, i) => (
            <div key={i} className="rounded-xl border border-slate-200 bg-white p-4" data-testid="calendar-cycle">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-[#0E2A47]">{MONTHS[e.month - 1]} {e.year} — {payrollTypeLabel(e.group)}</div>
                <RiskBadge s={e.cycle_risk?.status} />
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-sm text-slate-600 sm:grid-cols-3">
                <div>Payroll Period: <b>{fmtDate(e.dates.period_start)} → {fmtDate(e.dates.period_end)}</b></div>
                <div>Scheduled Payday: <b>{fmtDate(e.dates.nominal_payday)}</b></div>
                <div>Expected Payment Date: <b>{fmtDate(e.dates.target_funding_date)}</b></div>
                <div>Send to QuickBooks By: <b>{fmtDate(e.dates.qb_submission_cutoff)}</b></div>
                <div>Preceding bank day: <b>{fmtDate(e.dates.preceding_banking_day)}</b></div>
                <div>Next bank day: <b>{fmtDate(e.dates.next_available_banking_day)}</b></div>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {e.milestones.map((m) => (
                  <span key={m.key} className="rounded bg-slate-50 px-2 py-1 text-xs text-slate-600 border border-slate-200">
                    {m.label} · {m.role} · {fmtDate(m.due_date)}
                  </span>
                ))}
              </div>
            </div>
          ))}
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="mb-2 text-sm font-semibold text-slate-700">Banking Holidays & TFS Closures</div>
            <div className="flex flex-wrap gap-2">
              {calendar.filter((e) => e.type !== 'pay_cycle').map((e, i) => (
                <span key={i} data-testid="calendar-holiday"
                  className={`rounded px-2 py-1 text-xs border ${e.type === 'tfs_closure' ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
                  {fmtDate(e.date)} · {e.title}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ---- REMINDERS ---- */}
      {tab === 'reminders' && (
        <div className="mt-4 space-y-2" data-testid="payroll-reminders">
          {reminders.length === 0 ? <div className="p-8 text-center text-slate-500" data-testid="reminders-empty">No upcoming payroll reminders for your role.</div> :
            reminders.map((r, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid="reminder-item">
                <div>
                  <div className="text-sm font-medium text-[#0E2A47]">{r.label} <span className="text-slate-400">· {r.role}</span></div>
                  <div className="text-sm text-slate-500">{payrollTypeLabel(r.group)} · {fmtDateStr(r.period)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-500"><Clock size={12} className="mr-1 inline" />{fmtDate(r.due_date)}</span>
                  {r.state === 'past_due' && <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-sm font-semibold text-red-700"><AlertTriangle size={12} />Past Due → GA</span>}
                  {r.state === 'due_today' && <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-sm font-semibold text-amber-700"><Bell size={12} />Due Today</span>}
                  {r.state === 'completed' && <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-sm font-semibold text-emerald-700"><CheckCircle2 size={12} />Completed</span>}
                  {r.state === 'upcoming' && <RiskBadge s={r.cycle_risk} />}
                </div>
              </div>
            ))}
        </div>
      )}

      {/* ---- SUMMARY PACKET ---- */}
      {tab === 'packet' && (
        <div className="mt-4" data-testid="payroll-packet">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-sm font-medium text-slate-600">Year</label>
              <input type="number" className={FIELD} data-testid="packet-year" value={pk.year}
                onChange={(e) => setPk((p) => ({ ...p, year: Number(e.target.value) }))} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600">Month</label>
              <select className={FIELD} data-testid="packet-month" value={pk.month}
                onChange={(e) => setPk((p) => ({ ...p, month: Number(e.target.value) }))}>
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <Button onClick={loadPacket} data-testid="packet-load" className="bg-[#0E2A47] hover:bg-[#123a63]">Load Packet</Button>
            {packet && packet.runs.length > 0 && (
              <Button variant="outline" onClick={openPacketPdf} data-testid="packet-pdf"><FileText size={15} className="mr-1" />Open / Print PDF</Button>
            )}
          </div>
          <p className="mt-2 text-sm text-slate-500">The end-of-month cycle links the Second-Half and Monthly runs of the selected month into one consolidated packet.</p>

          {packet && (
            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="packet-body">
              <div className="text-xl font-bold text-[#0E2A47]">{packet.month_name} {packet.year} — Second-Half and Monthly Payroll</div>
              <div className="text-sm text-slate-500">Second-Half and Monthly payroll combined</div>
              {packet.runs.length === 0 ? (
                <div className="mt-3 text-sm text-slate-500" data-testid="packet-empty">No Second-Half or Monthly runs for this period.</div>
              ) : (
                <>
                  <div className="mt-3 grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-3 text-sm sm:grid-cols-6" data-testid="packet-combined">
                    <div>People and Companies: <b>{packet.combined.payees}</b></div>
                    <div>Regular Pay: <b>${packet.combined.base.toFixed(2)}</b></div>
                    <div>Deductions: <b>${packet.combined.deductions.toFixed(2)}</b></div>
                    <div>Additional Pay: <b>${packet.combined.additional.toFixed(2)}</b></div>
                    <div>Adjustments: <b>${packet.combined.adjustments.toFixed(2)}</b></div>
                    <div>Total Gross Pay: <b data-testid="packet-final-gross">${packet.combined.final.toFixed(2)}</b></div>
                  </div>
                  {packet.by_account.length > 0 && (
                    <div className="mt-3 text-sm" data-testid="packet-by-account">
                      <div className="font-semibold text-slate-600">Totals by account</div>
                      {packet.by_account.map((a) => (
                        <div key={a.account_number || 'na'}>{a.account_number || '—'} · {a.account_name || '—'}: <b>${a.gross.toFixed(2)}</b> ({a.payees})</div>
                      ))}
                    </div>
                  )}
                  {packet.runs.map((r) => (
                    <div key={r.id} className="mt-3 rounded-lg border border-slate-200 p-3" data-testid={`packet-run-${r.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-[#0E2A47]">{payrollTypeLabel(r.group)} — {fmtDate(r.period_start)} → {fmtDate(r.period_end)}</div>
                        <StatusBadge s={r.status} />
                      </div>
                      <div className="text-sm text-slate-500">Total Gross Pay <b>${r.totals.final_gross.toFixed(2)}</b>{r.qb_total != null ? ` · QuickBooks total $${r.qb_total.toFixed(2)} · Difference $${(r.variance || 0).toFixed(2)}` : ''}</div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ---- CREATE DIALOG ---- */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="run-dialog">
          <DialogHeader>
            <DialogTitle>Create Draft Payroll Run</DialogTitle>
            <DialogDescription>Dates are derived automatically. Confirm the funding target for unusual weekend/holiday cycles.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-600">Payroll Type</label>
              <select className={FIELD} data-testid="run-group" value={form.group} onChange={(e) => onField('group', e.target.value)}>
                <option value="first_half">First-Half Payroll</option>
                <option value="second_half">Second-Half Payroll</option>
                <option value="monthly">Monthly Payroll</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600">Year</label>
              <input type="number" className={FIELD} data-testid="run-year" value={form.year} onChange={(e) => onField('year', e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600">Month</label>
              <select className={FIELD} data-testid="run-month" value={form.month} onChange={(e) => onField('month', e.target.value)}>
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
          </div>

          {preview && (
            <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600" data-testid="run-preview">
              <div className="grid grid-cols-2 gap-1">
                <div>Payroll Period: <b>{fmtDate(preview.dates.period_start)} → {fmtDate(preview.dates.period_end)}</b></div>
                <div>Scheduled Payday: <b>{fmtDate(preview.dates.nominal_payday)}</b></div>
                <div>Expected Payment Date: <b data-testid="preview-target-funding">{fmtDate(form.target_funding_date || preview.dates.target_funding_date)}</b></div>
                <div>Send to QuickBooks By: <b data-testid="preview-qb-cutoff">{fmtDate(preview.dates.qb_submission_cutoff)}</b></div>
              </div>
              {preview.dates.needs_funding_confirmation && (
                <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2" data-testid="funding-confirm">
                  <div className="font-semibold text-amber-800">Payday falls on a non-banking day — choose funding target:</div>
                  <label className="mt-1 flex items-center gap-2">
                    <input type="radio" name="ft" data-testid="funding-preceding"
                      checked={form.target_funding_date === preview.dates.preceding_banking_day}
                      onChange={() => chooseFunding(preview.dates.preceding_banking_day)} />
                    Early opportunity — {fmtDate(preview.dates.preceding_banking_day)} (preceding banking day)
                  </label>
                  <label className="mt-1 flex items-center gap-2">
                    <input type="radio" name="ft" data-testid="funding-next"
                      checked={form.target_funding_date === preview.dates.next_available_banking_day}
                      onChange={() => chooseFunding(preview.dates.next_available_banking_day)} />
                    Handbook next business day — {fmtDate(preview.dates.next_available_banking_day)}
                  </label>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="run-cancel">Cancel</Button>
            <Button onClick={create} disabled={saving} data-testid="run-save" className="bg-[#0E2A47] hover:bg-[#123a63]">
              {saving ? 'Creating…' : 'Create Draft'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- RUN DETAIL / PREPARATION WORKSPACE (full-width) ---- */}
      {detail && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-50" data-testid="run-detail-dialog">
          <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:px-6">
            <div className="mx-auto flex max-w-[1600px] flex-wrap items-center justify-between gap-2">
              <div>
                <div className="flex flex-wrap items-center gap-2 text-lg font-bold text-[#0E2A47]">{payrollTypeLabel(detail.group)} — {fmtDate(detail.period_start)} → {fmtDate(detail.period_end)} <StatusBadge s={detail.status} /> <RiskBadge s={detail.cycle_risk?.status} /></div>
                <div className="text-xs text-slate-500">Scheduled Payday {fmtDate(detail.nominal_payday)} · Expected Payment {fmtDate(detail.target_funding_date)} · Send to QuickBooks By {fmtDate(detail.qb_submission_cutoff)} · {detail.payee_count} people and companies</div>
              </div>
              <button className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50" data-testid="close-workspace" onClick={() => setDetail(null)}>Close ✕</button>
            </div>
          </div>
          <div className="mx-auto max-w-[1600px] px-4 py-5 pb-28 sm:px-6">
            <>
              <div className="mb-3 text-sm font-semibold text-slate-700">Payroll Deadline Steps</div>
              <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-4" data-testid="run-milestones">
                {(detail.milestones || []).map((m) => (
                  <div key={m.key} className="flex items-center justify-between rounded border border-slate-200 px-3 py-1.5 text-sm">
                    <span>{m.label} <span className="text-slate-400">· {m.role}</span></span>
                    <span className={mstate(m.state).c} data-testid={`milestone-state-${m.key}`}>{fmtDate(m.due_date)} · {mstate(m.state).t}</span>
                  </div>
                ))}
              </div>

              <div className="mt-4">
                {(!detail.entries || detail.entries.length === 0) ? (
                  <div className="rounded border border-slate-200 p-4 text-sm text-slate-500" data-testid="run-empty-note">{detail.empty_reason || 'No payees in this run yet.'}</div>
                ) : (
                  <PrepWorkspace
                    ws={detail.workspace || { accounts: [], summary: {}, holidays: [] }}
                    entries={detail.entries || []}
                    sel={selAcct}
                    setSel={setSelAcct}
                    canEnter={canEnter}
                    isDraft={detail.status === 'draft'}
                    onConfirmEntry={confirmEntry}
                    onConfirmAccount={confirmAccount}
                    onOpenEntry={openEntry}
                  />
                )}
              </div>
              {detail.totals && (
                  <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="run-totals">
                    <div className="grid grid-cols-2 gap-1 sm:grid-cols-5">
                      <div>People and Companies: <b>{detail.totals.payees}</b></div>
                      <div>Regular Pay: <b>${detail.totals.base_compensation.toFixed(2)}</b></div>
                      <div>Deductions: <b>${detail.totals.deductions.toFixed(2)}</b></div>
                      <div>Additional Pay: <b>${(detail.totals.additional_pay + (detail.totals.adjustments || 0)).toFixed(2)}</b></div>
                      <div>Total Gross Pay: <b data-testid="run-final-gross">${detail.totals.final_gross.toFixed(2)}</b></div>
                    </div>
                    <div className="mt-2 border-t border-slate-200 pt-2">
                      <div className="font-semibold text-slate-600">Totals by account</div>
                      {detail.totals.totals_by_account.length === 0 ? (
                        <div className="text-slate-400" data-testid="acct-total-empty">No account totals yet</div>
                      ) : detail.totals.totals_by_account.map((a) => (
                        <div key={a.account_number} data-testid={`acct-total-${a.account_number}`}>{a.account_number} · {a.account_name}: <b>${a.gross.toFixed(2)}</b> ({a.payees})</div>
                      ))}
                    </div>
                  </div>
                )}

              {/* ---- APPROVAL WORKFLOW ---- */}
              <div className="mt-3 rounded-lg border border-slate-200 p-3" data-testid="run-workflow">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-700">Payroll Approval Steps</div>
                  <StatusBadge s={detail.status} />
                </div>

                {detail.qb_total != null && (
                  <div className="mt-2 grid grid-cols-3 gap-2 rounded bg-slate-50 p-2 text-sm" data-testid="run-reconciliation">
                    <div>Prepared gross: <b>${(detail.our_gross_total ?? detail.totals?.final_gross ?? 0).toFixed(2)}</b></div>
                    <div>QuickBooks total: <b>${detail.qb_total.toFixed(2)}</b></div>
                    <div>Difference: <b className={varianceOk ? 'text-emerald-600' : 'text-red-600'} data-testid="run-variance">${(detail.variance || 0).toFixed(2)}</b></div>
                  </div>
                )}
                {detail.variance_override && (
                  <div className="mt-1 text-xs text-red-600" data-testid="run-variance-override">Difference override by {detail.variance_override_by_name}: {detail.variance_override_reason}</div>
                )}
                {/* QuickBooks report version history */}
                {(detail.qb_reports || []).length > 0 && (
                  <div className="mt-2 rounded border border-slate-200" data-testid="qb-version-history">
                    <div className="border-b border-slate-200 bg-slate-50 px-2 py-1 text-sm font-semibold text-slate-700">QuickBooks Report Versions</div>
                    <div className="divide-y divide-slate-100">
                      {[...detail.qb_reports].sort((a, b) => b.version - a.version).map((v) => (
                        <div key={v.version} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2 py-1.5 text-xs" data-testid={`qb-version-${v.version}`}>
                          <span className="font-semibold text-slate-700">v{v.version}</span>
                          {v.is_current && <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 font-semibold text-emerald-700" data-testid={`qb-version-current-${v.version}`}>Current</span>}
                          <button className="text-[#0E2A47] underline" data-testid={`qb-version-download-${v.version}`} onClick={() => viewQbReport(v.version)}>{v.filename}</button>
                          <span>QB ${Number(v.qb_total || 0).toFixed(2)}</span>
                          <span className={Math.abs(v.variance || 0) < 0.005 ? 'text-emerald-600' : 'text-red-600'}>Diff ${Number(v.variance || 0).toFixed(2)}</span>
                          <span className="text-slate-500">{v.uploaded_by_name} · {(v.uploaded_at || '').slice(0, 16).replace('T', ' ')}</span>
                          {v.replace_reason && <span className="w-full text-slate-500">Reason: {v.replace_reason}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* QuickBooks report upload (Payroll Clerk) — only AFTER Final Payroll Approval */}
                {canProcess && ['ready_for_qb_entry', 'entered_in_quickbooks'].includes(detail.status) && (
                  <div className="mt-2 space-y-2 rounded border border-indigo-200 bg-indigo-50 p-2" data-testid="qb-upload-panel">
                    <div className="text-sm font-semibold text-indigo-800">
                      Payroll Clerk: {(detail.qb_reports || []).length > 0 ? 'upload a new report version' : 'upload the QuickBooks pre-submission report'} and enter the QuickBooks total.
                    </div>
                    {detail.status === 'entered_in_quickbooks' && (
                      <div className="text-xs text-red-700" data-testid="qb-upload-invalidate-warning">Uploading a new version returns the run to Ready for Processing so it must be re-entered. Final Payroll Approval is preserved.</div>
                    )}
                    <input key={qbInputKey} type="file" data-testid="qb-report-input" className="text-sm"
                      onChange={(e) => setQbFile(e.target.files[0] || null)} />
                    <div className="flex flex-wrap items-center gap-2">
                      <input type="number" step="any" min="0" placeholder="QuickBooks total ($)" data-testid="qb-total-input"
                        className="rounded border border-slate-300 px-2 py-1 text-sm" value={qbTotal} onChange={(e) => setQbTotal(e.target.value)} />
                      {(detail.qb_reports || []).length > 0 && (
                        <input placeholder="Reason for replacing current report" data-testid="qb-replace-reason"
                          className="min-w-[220px] flex-1 rounded border border-slate-300 px-2 py-1 text-sm" value={replaceReason} onChange={(e) => setReplaceReason(e.target.value)} />
                      )}
                      <Button size="sm" data-testid="qb-upload-btn" disabled={!qbFile || qbTotal === ''}
                        onClick={uploadQbReport} className="bg-[#0E2A47] hover:bg-[#123a63]">Upload Report Version</Button>
                    </div>
                  </div>
                )}

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {detail.status === 'draft' && (
                    <div className="text-xs text-slate-400">Use the account panels above to confirm each account, then complete preparation from the bar at the bottom.</div>
                  )}
                  {detail.status === 'prepared' && canPrepare && <Button size="sm" data-testid="wf-submit" onClick={() => doTransition('submit')} className="bg-[#0E2A47] hover:bg-[#123a63]">Submit for Final Payroll Approval</Button>}

                  {detail.status === 'pending_owner_approval' && canApprove && (
                    <div className="w-full rounded border border-amber-200 bg-amber-50 p-2" data-testid="wf-approve-panel">
                      <div className="text-sm font-semibold text-amber-800">Final Payroll Approval — Owner authorization required. Processing / QuickBooks entry unlocks only after this step.</div>
                      <Button size="sm" className="mt-2 bg-emerald-700 hover:bg-emerald-800" data-testid="wf-approve"
                        onClick={() => doTransition('approve')}>Provide Final Payroll Approval</Button>
                    </div>
                  )}
                  {detail.status === 'pending_owner_approval' && !canApprove && (
                    <div className="w-full rounded border border-slate-200 bg-slate-50 p-2 text-sm text-slate-600" data-testid="wf-awaiting-approval">
                      Awaiting Final Payroll Approval by the Owner Global Administrator. Processing is locked until then.
                    </div>
                  )}

                  {detail.status === 'approved_submitted' && canProcess && (
                    <Button size="sm" data-testid="wf-ready" onClick={() => doTransition('ready_for_qb')} className="bg-[#0E2A47] hover:bg-[#123a63]">Mark Ready for Processing</Button>
                  )}

                  {detail.status === 'ready_for_qb_entry' && canProcess && (
                    <div className="w-full" data-testid="wf-enter-qb-panel">
                      {!varianceOk && detail.qb_report && (
                        <div className="mb-1 space-y-1">
                          <div className="text-sm text-red-700" data-testid="wf-variance-warning">A ${(detail.variance || 0).toFixed(2)} difference remains vs QuickBooks. Record a written override to proceed.</div>
                          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="wf-override" checked={override} onChange={(e) => setOverride(e.target.checked)} /> Override non-zero difference</label>
                          {override && <input placeholder="Override reason" data-testid="wf-override-reason" className="w-full rounded border border-slate-300 px-2 py-1 text-sm" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />}
                        </div>
                      )}
                      <Button size="sm" data-testid="wf-enter-qb" disabled={!detail.qb_report || (!varianceOk && (!override || !overrideReason.trim()))}
                        onClick={() => doTransition('enter_qb', { override, reason: overrideReason })} className="bg-[#0E2A47] hover:bg-[#123a63]">Enter in QuickBooks</Button>
                      {!detail.qb_report && <span className="ml-2 text-xs text-slate-500">Upload the report above to enable entry.</span>}
                    </div>
                  )}

                  {detail.status === 'entered_in_quickbooks' && canApprove && <Button size="sm" data-testid="wf-close" onClick={() => doTransition('close')} className="bg-[#0E2A47] hover:bg-[#123a63]">Close Run</Button>}
                  {detail.status === 'closed' && <span className="text-sm text-slate-500">This run is closed.</span>}
                </div>

                {canApprove && detail.status !== 'draft' && (
                  <div className="mt-3 border-t border-slate-200 pt-2" data-testid="wf-reopen-panel">
                    <div className="flex items-center gap-1 text-sm font-semibold text-slate-600"><RotateCcw size={12} /> Return / Reopen (Global Administrator)</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <select className="rounded border border-slate-300 px-2 py-1 text-sm" data-testid="reopen-target" value={reopenTo} onChange={(e) => setReopenTo(e.target.value)}>
                        <option value="">Return to…</option>
                        {reopenTargets(detail.status).map((s) => <option key={s} value={s}>{STATUS[s].t}</option>)}
                      </select>
                      <input placeholder="Reason" className="rounded border border-slate-300 px-2 py-1 text-sm" data-testid="reopen-reason" value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} />
                      <Button size="sm" variant="outline" data-testid="wf-reopen" disabled={!reopenTo || !reopenReason.trim()} onClick={reopenRun}>Reopen</Button>
                    </div>
                  </div>
                )}

                {(detail.status_history || []).length > 0 && (
                  <div className="mt-3 border-t border-slate-200 pt-2" data-testid="run-history">
                    <div className="text-sm font-semibold text-slate-600">Audit History</div>
                    {(detail.status_history || []).map((hh, i) => (
                      <div key={i} className="text-xs text-slate-500">{hh.label || hh.action} — {hh.by_name} · {(hh.at || '').slice(0, 19).replace('T', ' ')}{hh.reason ? ` · ${hh.reason}` : ''}</div>
                    ))}
                  </div>
                )}
              </div>
            </>
          </div>
          {/* Sticky completion action bar */}
          {detail.status === 'draft' && canPrepare && (
            <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-2px_10px_rgba(0,0,0,0.06)] backdrop-blur sm:px-6" data-testid="sticky-complete-bar">
              <div className="mx-auto flex max-w-[1600px] flex-wrap items-center justify-between gap-3 pl-40 sm:pl-44">
                <div className="text-sm text-slate-600" data-testid="complete-progress">
                  {detail.workspace?.summary?.accounts_confirmed || 0} of {detail.workspace?.summary?.accounts_total || 0} accounts confirmed · {detail.workspace?.summary?.exceptions || 0} exception(s) remaining
                  {(detail.workspace?.accounts || []).some((a) => a.budget_state === 'under' || a.budget_state === 'over') && (
                    <label className="ml-3 inline-flex items-center gap-1 text-xs text-amber-700" data-testid="ack-warnings">
                      <input type="checkbox" checked={ackWarn} onChange={(e) => setAckWarn(e.target.checked)} /> Acknowledge budget warnings
                    </label>
                  )}
                </div>
                <Button size="sm" data-testid="wf-prepare-sticky"
                  disabled={!detail.workspace?.summary?.can_complete || ((detail.workspace?.accounts || []).some((a) => a.budget_state === 'under' || a.budget_state === 'over') && !ackWarn)}
                  className="bg-[#0E2A47] hover:bg-[#123a63] disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600"
                  onClick={() => doTransition('prepare', { acknowledge_warnings: ackWarn })}>
                  Mark Payroll Preparation Complete
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Gross-pay entry editor */}
      <Dialog open={!!editEntry} onOpenChange={(o) => !o && setEditEntry(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="entry-editor-dialog">
          {editEntry && (() => {
            const b = editEntry.rate_basis;
            const locked = detail?.status !== 'draft';
            const num = (label, key, ph) => (
              <label className="block text-sm">{label}
                <input type="number" step="any" min="0" data-testid={`act-${key}`} disabled={locked}
                  className="mt-1 w-full rounded border border-slate-300 px-2 py-1 disabled:bg-slate-100"
                  value={act[key] ?? ''} placeholder={ph || ''}
                  onChange={(e) => setAct((p) => ({ ...p, [key]: e.target.value === '' ? '' : Number(e.target.value) }))} />
              </label>);
            return (
              <>
                <DialogHeader>
                  <DialogTitle>{editEntry.payee_name} — {editEntry.account_number} · {editEntry.account_name}</DialogTitle>
                  <DialogDescription>{RATE_BASIS_LABELS[b] || b} · {locked ? 'entries are locked (run is beyond Draft)' : 'enter actuals to compute gross'}</DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                  {b === 'hourly' && num('Actual hours', 'hours')}
                  {b === 'daily' && num('Approved payable days', 'payable_days')}
                  {b === 'flat_unit' && num(`Approved units${editEntry.unit_label ? ` (${editEntry.unit_label})` : ''}`, 'units')}
                  {b === 'monthly_location' && (<>
                    <div className="rounded bg-slate-50 p-2 text-sm">Approved monthly payout <b>${editEntry.approved_monthly_payout}</b> · Auto normal labor days <b data-testid="auto-normal-days">{editEntry.normal_labor_days_auto}</b> (holidays deducted after the daily rate)</div>
                    {num('Confirm/adjust normal labor days', 'normal_labor_days', String(editEntry.normal_labor_days_auto))}
                    {num('Holidays not worked', 'holidays_not_worked')}
                    {num('Other missed days', 'missed_days')}
                    {num('Approved additional days', 'additional_days')}
                    <label className="block text-sm">Reason (required if changing normal days)
                      <input data-testid="act-reason" disabled={locked} className="mt-1 w-full rounded border border-slate-300 px-2 py-1 disabled:bg-slate-100" value={act._reason || ''} onChange={(e) => setAct((p) => ({ ...p, _reason: e.target.value }))} /></label>
                  </>)}
                  {b === 'weekly_flat' && (<>
                    <div className="rounded bg-slate-50 p-2 text-sm">Weekly reference <b>${editEntry.weekly_reference_amount}</b> ÷ {editEntry.scheduled_days_per_week} = daily equivalent <b>${editEntry.daily_equivalent}</b>. TFS holidays are paid and already in the base ($0 deduction).</div>
                    {num('Approved Flat Base for This Pay Period', 'approved_period_base')}
                    {num('Non-holiday missed days (deduct daily equivalent)', 'other_missed_days')}
                    {num('Approved additional-day amount ($)', 'additional_day_amount')}
                    {num('Approved adjustments ($)', 'adjustments')}
                  </>)}
                  {editEntry.components && (
                    <div className="rounded border border-slate-200 p-2 text-sm" data-testid="entry-components">
                      <div className="font-semibold">Calculation</div>
                      {Object.entries(editEntry.components).map(([k, v]) => (<div key={k}>{k}: <b>{String(v)}</b></div>))}
                      <div className="mt-1 border-t pt-1">Base gross: <b>${(editEntry.base_gross ?? 0).toFixed?.(2) ?? editEntry.base_gross}</b> · Total gross: <b data-testid="editor-gross">${(editEntry.gross ?? 0).toFixed(2)}</b></div>
                    </div>
                  )}
                  {!locked && <div className="flex justify-end"><Button size="sm" onClick={saveActuals} data-testid="act-save">Save actuals</Button></div>}

                  <div className="rounded border border-slate-200 p-2">
                    <div className="mb-1 text-sm font-semibold">Additional pay</div>
                    {(editEntry.additional_pay || []).map((l) => (
                      <div key={l.id} className="text-sm" data-testid={`ap-line-${l.id}`}>+ ${l.amount.toFixed(2)} {l.kind} ({l.account_number}) {l.work_order_number ? `WO ${l.work_order_number}` : ''} — {l.reason}</div>
                    ))}
                    {!locked && (
                      <>
                        <div className="mt-1 grid grid-cols-2 gap-1">
                          <select className="rounded border border-slate-300 px-2 py-1 text-sm" data-testid="ap-kind" value={ap.kind} onChange={(e) => setAp((p) => ({ ...p, kind: e.target.value }))}>
                            {Object.entries(AP_KINDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                          <input type="number" step="any" min="0" placeholder="Amount" className="rounded border border-slate-300 px-2 py-1 text-sm" data-testid="ap-amount" value={ap.amount ?? ''} onChange={(e) => setAp((p) => ({ ...p, amount: Number(e.target.value) }))} />
                          <input placeholder="Account #" className="rounded border border-slate-300 px-2 py-1 text-sm" data-testid="ap-account" value={ap.account_number ?? ''} onChange={(e) => setAp((p) => ({ ...p, account_number: e.target.value }))} />
                          {ap.kind === 'work_order' && <input placeholder="Work-order / job #" className="rounded border border-slate-300 px-2 py-1 text-sm" data-testid="ap-wo" value={ap.work_order_number ?? ''} onChange={(e) => setAp((p) => ({ ...p, work_order_number: e.target.value }))} />}
                          <input placeholder="Reason" className="col-span-2 rounded border border-slate-300 px-2 py-1 text-sm" data-testid="ap-reason" value={ap.reason ?? ''} onChange={(e) => setAp((p) => ({ ...p, reason: e.target.value }))} />
                        </div>
                        <div className="mt-1 flex justify-end"><Button size="sm" variant="outline" onClick={addAdditional} data-testid="ap-add">Add</Button></div>
                      </>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayrollPayRuns;
